"use client";

import { List, RadioGroupRoot } from "@dafiti/closet-ui";
import type { ReactNode } from "react";

import { useFilterOptionList } from "../../../hooks/useFilterOptionList.hook";
import type { FacetValue } from "../../../types/filters.type";
import { FilterOptionListItem } from "./FilterOptionListItem";

type FilterOptionListProps = {
  options: FacetValue[];
  selectedValues: string[];
  selector: "Multiple" | "Single";
  hideDivider?: boolean;
  isCompact?: boolean;
  renderPrefix?: (option: FacetValue) => ReactNode;
  onSelectionChange: (values: string[]) => void;
};

export function FilterOptionList({
  options,
  selectedValues,
  selector,
  hideDivider = false,
  isCompact = false,
  renderPrefix,
  onSelectionChange,
}: FilterOptionListProps) {
  const { sortedOptions, selectedSet, handleToggle, handleRadioChange } =
    useFilterOptionList({
      options,
      selectedValues,
      selector,
      onSelectionChange,
    });

  const renderItems = () =>
    sortedOptions.map((option) => (
      <FilterOptionListItem
        key={option.value}
        value={option.value}
        label={option.label}
        checked={selectedSet.has(option.value)}
        selector={selector}
        hideDivider={hideDivider}
        isCompact={isCompact}
        prefix={renderPrefix?.(option)}
        onToggle={handleToggle}
      />
    ));

  if (selector === "Single") {
    return (
      <RadioGroupRoot
        value={selectedValues[0] ?? ""}
        onValueChange={handleRadioChange}
        data-testid="filter-option-list"
      >
        <List as="div" className="divide-y-0">
          {renderItems()}
        </List>
      </RadioGroupRoot>
    );
  }

  return (
    <List as="div" className="divide-y-0" data-testid="filter-option-list">
      {renderItems()}
    </List>
  );
}

export type { FilterOptionListProps };
